import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, LogOut } from "lucide-react";

const GRADES = ["Preschool", "JK", "SK", "G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8"];

const ApplicationPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [students, setStudents] = useState([
    { first_name: "", last_name: "", gender: "", date_of_birth: "", grade_applying: "", current_school: "" }
  ]);
  const [hasSibling, setHasSibling] = useState(false);
  const [siblingInfo, setSiblingInfo] = useState({ student_name: "", grade: "" });
  const [consent, setConsent] = useState(false);
  const [keepInJkWaitlist, setKeepInJkWaitlist] = useState(null); // null, true, or false
  const [loading, setLoading] = useState(false);
  const [myApplications, setMyApplications] = useState([]);

  useEffect(() => {
    fetchMyApplications();
  }, []);

  const fetchMyApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications/parent/${user.id}`);
      setMyApplications(response.data);
    } catch (error) {
      console.error("Failed to fetch applications", error);
    }
  };

  const addStudent = () => {
    setStudents([...students, { first_name: "", last_name: "", gender: "", date_of_birth: "", grade_applying: "", current_school: "" }]);
  };

  const removeStudent = (index) => {
    if (students.length > 1) {
      setStudents(students.filter((_, i) => i !== index));
    }
  };

  const handleStudentChange = (index, field, value) => {
    const updated = [...students];
    updated[index][field] = value;
    setStudents(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast.error("Please provide consent to submit");
      return;
    }

    const invalidStudents = students.filter(s => !s.first_name || !s.last_name || !s.gender || !s.date_of_birth || !s.grade_applying || !s.current_school);
    if (invalidStudents.length > 0) {
      toast.error("Please fill in all student information");
      return;
    }

    // Check if applying for preschool and JK waitlist preference is not set
    const hasPreschoolStudent = students.some(s => 
      s.grade_applying && s.grade_applying === "Preschool"
    );
    if (hasPreschoolStudent && keepInJkWaitlist === null) {
      toast.error("Please indicate if you want to remain in JK waitlist next year");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        parent_id: user.id,
        students: students,
        has_sibling: hasSibling,
        sibling_info: hasSibling && siblingInfo.student_name && siblingInfo.grade ? siblingInfo : null,
        consent: consent,
        keep_in_jk_waitlist_next_year: hasPreschoolStudent ? keepInJkWaitlist : null
      };

      const response = await axios.post(`${API}/applications`, payload);
      toast.success("Application submitted successfully!");
      navigate(`/application-success/${response.data.id}`);
    } catch (error) {
      console.error("Application submission error:", error);
      let errorMessage = "Failed to submit application";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle Pydantic validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(", ");
        } 
        // Handle string error messages
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object errors
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School" 
              className="h-12"
            />
            <div>
              <h2 className="text-[#0E366A] font-serif text-lg font-bold">London Islamic School</h2>
              <p className="text-[#0E366A] text-xs italic">Student Enrollment</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-[#0E366A] font-medium">Welcome, {user?.name}</span>
            <Button 
              data-testid="logout-btn"
              variant="ghost" 
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center">Student Enrollment Application</h1>

          {/* My Applications */}
          {myApplications.length > 0 && (
            <Card className="mb-8 bg-white">
              <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
                <CardTitle className="text-[#0E366A]">My Applications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myApplications.map((app, idx) => (
                    <div key={app.id || app.application_code || idx} className="bg-[#F7B538]/10 p-4 rounded-lg border border-[#F7B538]">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-[#0E366A]">Code: {app.application_code || 'N/A'}</p>
                          <p className="text-sm text-gray-700">Status: <span className="font-medium">{typeof app.status === 'string' ? app.status : JSON.stringify(app.status)}</span></p>
                          <p className="text-sm text-gray-700 mt-1">
                            Students: {app.students ? app.students.map(s => {
                              const grade = typeof s.grade_applying === 'object' ? JSON.stringify(s.grade_applying) : s.grade_applying;
                              return `${s.first_name} ${s.last_name} (${grade})`;
                            }).join(", ") : 'N/A'}
                          </p>
                          <p className="text-sm text-gray-700">
                            Positions: {app.waitlist_positions && typeof app.waitlist_positions === 'object' && Object.keys(app.waitlist_positions).length > 0 ? Object.entries(app.waitlist_positions).map(([grade, pos]) => `${grade}: #${pos}`).join(", ") : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Application Form */}
          <Card className="bg-white">
            <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
              <CardTitle className="text-[#0E366A]">New Application</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Students Section */}
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-[#0E366A]">Student Information</h3>
                    <Button 
                      data-testid="add-student-btn"
                      type="button" 
                      onClick={addStudent}
                      variant="outline"
                      className="border-[#F7B538] text-[#0E366A] hover:bg-[#F7B538]/20"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Another Student
                    </Button>
                  </div>

                  {students.map((student, index) => (
                    <Card key={index} className="bg-[#F7B538]/5 border-[#F7B538]/30">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-[#0E366A]">Student {index + 1}</h4>
                          {students.length > 1 && (
                            <Button 
                              data-testid={`remove-student-${index}-btn`}
                              type="button" 
                              onClick={() => removeStudent(index)}
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">First Name *</Label>
                            <Input 
                              data-testid={`student-${index}-firstname`}
                              required
                              value={student.first_name}
                              onChange={(e) => handleStudentChange(index, 'first_name', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">Last Name *</Label>
                            <Input 
                              data-testid={`student-${index}-lastname`}
                              required
                              value={student.last_name}
                              onChange={(e) => handleStudentChange(index, 'last_name', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">Gender *</Label>
                            <Select 
                              value={student.gender}
                              onValueChange={(value) => handleStudentChange(index, 'gender', value)}
                            >
                              <SelectTrigger data-testid={`student-${index}-gender`}>
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                              <SelectContent className="z-[9999]">
                                <SelectItem value="Male">Male</SelectItem>
                                <SelectItem value="Female">Female</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">Date of Birth *</Label>
                            <Input 
                              data-testid={`student-${index}-dob`}
                              type="date"
                              required
                              value={student.date_of_birth}
                              onChange={(e) => handleStudentChange(index, 'date_of_birth', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">Grade Applying For (September) *</Label>
                            <Select 
                              value={student.grade_applying}
                              onValueChange={(value) => handleStudentChange(index, 'grade_applying', value)}
                            >
                              <SelectTrigger data-testid={`student-${index}-grade`}>
                                <SelectValue placeholder="Select grade" />
                              </SelectTrigger>
                              <SelectContent className="z-[9999]">
                                {GRADES.map(grade => (
                                  <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#0E366A]">Current School Name *</Label>
                            <Input 
                              data-testid={`student-${index}-school`}
                              required
                              value={student.current_school}
                              onChange={(e) => handleStudentChange(index, 'current_school', e.target.value)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Sibling Section */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-[#0E366A]">Sibling Information</h3>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      data-testid="has-sibling-checkbox"
                      id="hasSibling" 
                      checked={hasSibling}
                      onCheckedChange={setHasSibling}
                    />
                    <Label htmlFor="hasSibling" className="cursor-pointer text-[#0E366A]">
                      Does the applicant have a sibling currently enrolled at London Islamic School?
                    </Label>
                  </div>

                  {hasSibling && (
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-[#0E366A]">Sibling's Name</Label>
                        <Input 
                          data-testid="sibling-name-input"
                          value={siblingInfo.student_name}
                          onChange={(e) => setSiblingInfo({...siblingInfo, student_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0E366A]">Sibling's Current Grade</Label>
                        <Select 
                          value={siblingInfo.grade}
                          onValueChange={(value) => setSiblingInfo({...siblingInfo, grade: value})}
                        >
                          <SelectTrigger data-testid="sibling-grade-select">
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            {GRADES.map(grade => (
                              <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                {/* JK Waitlist Preference Section - Only for Preschool */}
                {students.some(s => s.grade_applying && s.grade_applying === "Preschool") && (
                  <div className="space-y-4 bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-xl font-semibold text-[#0E366A]">JK Waitlist Preference</h3>
                    <p className="text-sm text-gray-600">
                      Since you are applying for a Preschool spot, would you like to remain in the JK (Junior Kindergarten) waitlist next year?
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id="jk-yes"
                          name="jk-waitlist"
                          checked={keepInJkWaitlist === true}
                          onChange={() => setKeepInJkWaitlist(true)}
                          className="w-4 h-4 text-[#0E366A] border-gray-300 focus:ring-[#F7B538]"
                        />
                        <Label htmlFor="jk-yes" className="cursor-pointer text-[#0E366A] font-medium">
                          Yes, keep me in JK waitlist next year
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id="jk-no"
                          name="jk-waitlist"
                          checked={keepInJkWaitlist === false}
                          onChange={() => setKeepInJkWaitlist(false)}
                          className="w-4 h-4 text-[#0E366A] border-gray-300 focus:ring-[#F7B538]"
                        />
                        <Label htmlFor="jk-no" className="cursor-pointer text-[#0E366A] font-medium">
                          No, do not move me to JK waitlist next year
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Consent Section */}
                <div className="space-y-4 bg-[#F7B538]/10 p-6 rounded-lg border border-[#F7B538]">
                  <h3 className="text-xl font-semibold text-[#0E366A]">Consent</h3>
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      data-testid="consent-checkbox"
                      id="consent" 
                      checked={consent}
                      onCheckedChange={setConsent}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="cursor-pointer text-sm leading-relaxed text-[#0E366A]">
                      I hereby consent and confirm that all information provided in this application is accurate and complete. I understand that London Islamic School will use this information for enrollment purposes and to contact me regarding my application.
                    </Label>
                  </div>
                </div>

                <Button 
                  data-testid="submit-application-btn"
                  type="submit" 
                  className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] py-6 text-lg font-semibold"
                  disabled={loading || !consent}
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;